import { ErrorNotice } from "../../shell/ErrorNotice";
import { t } from "../../i18n";
import { isFlagged, type QueueState } from "./queue-state";
import { ageOf, describeRow } from "./verification-format";
import {
  DOCUMENT_FILTERS,
  STATUS_FILTERS,
  verificationsHref,
  type DocumentFilter,
  type StatusFilter,
  type VerificationFilters,
} from "./verification-route";

export type QueueViewProps = {
  filters: VerificationFilters;
  selectedId: string | null;
  queue: QueueState;
  onFilters: (filters: VerificationFilters) => void;
  onRetry: () => void;
  onLoadMore: () => void;
  now?: Date;
};

/** The header's line: how many wait, and for how long the oldest has. */
export function queueSummary(filters: VerificationFilters, queue: QueueState, now: Date = new Date()): string {
  if (queue.status !== "ready") return t("sub.verifications");
  const count = queue.nextCursor ? `${queue.items.length}+` : String(queue.items.length);
  if (filters.status !== "open") return t("verifications.summary.shown", { count });
  const oldest = queue.items[0];
  if (!oldest) return t("verifications.summary.none");
  return t("verifications.summary.open", { count, age: ageOf(oldest.submittedAt, now).text });
}

/** The filters, then the queue oldest first; a banned document stands out. */
export function QueueView({ filters, selectedId, queue, onFilters, onRetry, onLoadMore, now }: QueueViewProps) {
  return (
    <div className="card verif-queue">
      <div className="form verif-filters">
        <div className="field">
          <label htmlFor="verif-status">{t("verifications.filter.status")}</label>
          <select
            id="verif-status"
            value={filters.status}
            onChange={(event) => onFilters({ ...filters, status: event.target.value as StatusFilter })}
          >
            {STATUS_FILTERS.map((status) => (
              <option key={status} value={status}>
                {t(`verifications.filter.status.${status}` as const)}
              </option>
            ))}
          </select>
        </div>
        <div className="field">
          <label htmlFor="verif-document">{t("verifications.filter.document")}</label>
          <select
            id="verif-document"
            value={filters.documentType}
            onChange={(event) => onFilters({ ...filters, documentType: event.target.value as DocumentFilter })}
          >
            {DOCUMENT_FILTERS.map((type) => (
              <option key={type} value={type}>
                {t(`verifications.filter.document.${type}` as const)}
              </option>
            ))}
          </select>
        </div>
      </div>

      {queue.status === "loading" ? (
        <div className="empty" role="status">
          <span className="spinner" aria-hidden="true" />
          <span>{t("verifications.queue.loading")}</span>
        </div>
      ) : queue.status === "error" ? (
        <div className="audit-error">
          <p className="foot">{t("verifications.queue.loadFailed")}</p>
          <ErrorNotice error={queue.error} />
          <div>
            <button className="btn primary" type="button" onClick={onRetry}>
              {t("audit.retry")}
            </button>
          </div>
        </div>
      ) : queue.items.length === 0 ? (
        <p className="foot" role="status">
          {filters.status === "open" ? t("verifications.queue.emptyOpen") : t("verifications.queue.empty")}
        </p>
      ) : (
        <div className="rows" aria-label={t("verifications.queue.aria")}>
          {queue.items.map((item) => {
            const flagged = isFlagged(item);
            const age = ageOf(item.submittedAt, now);
            const selected = item.identityVerificationId === selectedId;
            return (
              <a
                key={item.identityVerificationId}
                href={verificationsHref(filters, item.identityVerificationId)}
                className={`verif-row${selected ? " sel" : ""}${flagged ? " flagged" : ""}`}
                aria-current={selected ? "true" : undefined}
                data-verification={item.identityVerificationId}
              >
                <span>
                  <b>{item.pro.name}</b>
                  <br />
                  <span className="meta">{describeRow(item)}</span>
                </span>
                <span className="verif-pills">
                  {flagged ? <span className="pill bad">{t("verifications.flag.banned")}</span> : null}
                  {item.status === "approved" || item.status === "rejected" ? (
                    <span className={`pill ${item.status === "approved" ? "ok" : "mute"}`}>
                      {t(`verifications.status.${item.status}` as const)}
                    </span>
                  ) : null}
                  <span className={`pill ${age.tone}`} title={t("verifications.queue.waited")}>
                    {age.text}
                  </span>
                </span>
              </a>
            );
          })}
        </div>
      )}

      {queue.status === "ready" && queue.items.length > 0 ? (
        <div className="audit-more">
          {queue.moreError ? <ErrorNotice error={queue.moreError} /> : null}
          <div className="btns">
            {queue.nextCursor ? (
              <button className="btn" type="button" onClick={onLoadMore} disabled={queue.loadingMore}>
                {queue.loadingMore
                  ? t("customers.search.loadingMore")
                  : queue.moreError
                    ? t("customers.search.retry")
                    : t("customers.search.loadMore")}
              </button>
            ) : (
              <span className="meta">{t("verifications.queue.end")}</span>
            )}
          </div>
        </div>
      ) : null}
    </div>
  );
}
